import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, ArrowLeft, Eye, Lock, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import LimiarScreen from "../components/case/LimiarScreen";
import StepAnexos from "../components/case/StepAnexos";
import StepClassificacaoMetodologica from "../components/case/StepClassificacaoMetodologica";
import StepConsolidacao from "../components/case/StepConsolidacao";
import StepEncerramento from "../components/case/StepEncerramento";
import StepEvidencias from "../components/case/StepEvidencias";
import StepHipoteses from "../components/case/StepHipoteses";
import StepIdentificacao from "../components/case/StepIdentificacao";
import StepManifestacoes from "../components/case/StepManifestacoes";
import StepMetodologia from "../components/case/StepMetodologia";
import StepNavigation from "../components/case/StepNavigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CasePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, canCreateProjects } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isReadOnly = !canCreateProjects();

  const fetchCase = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cases/${id}`, { headers: getAuthHeaders() });
      setCaseData(response.data);
    } catch {
      toast.error("Erro ao carregar caso");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders, navigate]);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  const saveCase = async (updates, showToast = true) => {
    if (isReadOnly) {
      toast.error("Seu acesso está inativo. Regularize sua assinatura para editar projetos.");
      return null;
    }
    setSaving(true);
    try {
      const response = await axios.put(`${API}/cases/${id}`, updates, { headers: getAuthHeaders() });
      setCaseData(response.data);
      if (showToast) toast.success("Alterações salvas");
      return response.data;
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Assinatura inativa. Regularize para editar projetos.");
        navigate("/regularizacao");
      } else {
        toast.error("Erro ao salvar");
      }
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const updateCaseData = (field, value) => {
    if (isReadOnly) return;
    setCaseData((prev) => ({ ...prev, [field]: value }));
  };

  const goToStep = async (step) => {
    if (isReadOnly) {
      setCaseData((prev) => ({ ...prev, etapa_atual: step }));
      return;
    }
    await saveCase({ etapa_atual: step }, false);
  };

  const completeLimiar = async () => {
    if (isReadOnly) {
      toast.error("Seu acesso está inativo. Regularize sua assinatura para continuar.");
      navigate("/regularizacao");
      return;
    }
    await saveCase({ limiar_concluido: true, etapa_atual: 1 });
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F5F2EB] flex items-center justify-center"><p className="text-[#718096] font-serif text-xl">Carregando...</p></div>;
  }
  if (!caseData) return null;

  if (!caseData.limiar_concluido) {
    if (isReadOnly) {
      return (
        <div className="min-h-screen bg-[#F5F2EB] flex flex-col items-center justify-center p-8">
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 max-w-md text-center">
            <Lock size={48} className="mx-auto text-amber-500 mb-4" />
            <h2 className="font-serif text-xl font-medium text-[#1A202C] mb-2">Acesso Restrito</h2>
            <p className="text-[#718096] mb-6">Este caso ainda não foi iniciado e sua assinatura está inativa. Regularize para continuar.</p>
            <div className="space-y-3">
              <Button onClick={() => navigate("/regularizacao")} className="w-full">Regularizar Assinatura</Button>
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full">Voltar ao Painel</Button>
            </div>
          </div>
        </div>
      );
    }
    return <LimiarScreen onStart={completeLimiar} />;
  }

  const commonOnSave = () => toast.error("Modo somente leitura. Regularize sua assinatura para editar.");

  const renderStep = () => {
    switch (caseData.etapa_atual) {
      case 1:
        return <StepIdentificacao caseId={id} identificacao={caseData.identificacao} onChange={(value) => updateCaseData("identificacao", value)} onSave={isReadOnly ? commonOnSave : () => saveCase({ identificacao: caseData.identificacao })} readOnly={isReadOnly} />;
      case 2:
        return <StepManifestacoes manifestacoes={caseData.manifestacoes} onChange={(value) => updateCaseData("manifestacoes", value)} onSave={isReadOnly ? commonOnSave : () => saveCase({ manifestacoes: caseData.manifestacoes })} readOnly={isReadOnly} />;
      case 3:
        return <StepEvidencias evidencias={caseData.evidencias} onChange={(value) => updateCaseData("evidencias", value)} onSave={isReadOnly ? commonOnSave : () => saveCase({ evidencias: caseData.evidencias })} readOnly={isReadOnly} />;
      case 4:
        return <StepHipoteses hipoteses={caseData.hipoteses} onChange={(value) => updateCaseData("hipoteses", value)} onSave={isReadOnly ? commonOnSave : () => saveCase({ hipoteses: caseData.hipoteses })} readOnly={isReadOnly} />;
      case 5:
        return <StepConsolidacao consolidacao={caseData.consolidacao} onChange={(value) => updateCaseData("consolidacao", value)} onSave={isReadOnly ? commonOnSave : () => saveCase({ consolidacao: caseData.consolidacao })} readOnly={isReadOnly} />;
      case 6:
        return <StepMetodologia metodologia={caseData.metodologia} onChange={(value) => updateCaseData("metodologia", value)} onSave={isReadOnly ? commonOnSave : () => saveCase({ metodologia: caseData.metodologia })} readOnly={isReadOnly} />;
      case 7:
        return <StepAnexos anexos={caseData.anexos} onChange={(value) => updateCaseData("anexos", value)} onSave={isReadOnly ? commonOnSave : () => saveCase({ anexos: caseData.anexos })} hasManifestacoes={caseData.manifestacoes?.length > 0} readOnly={isReadOnly} />;
      case 8:
        return <StepClassificacaoMetodologica classificacao={caseData.classificacao_metodologica} onChange={(value) => updateCaseData("classificacao_metodologica", value)} onSave={isReadOnly ? commonOnSave : () => saveCase({ classificacao_metodologica: caseData.classificacao_metodologica })} readOnly={isReadOnly} />;
      case 9:
        return <StepEncerramento caseId={id} encerramento={caseData.encerramento} onChange={(value) => updateCaseData("encerramento", value)} onSave={isReadOnly ? commonOnSave : (data) => saveCase({ encerramento: data })} getAuthHeaders={getAuthHeaders} readOnly={isReadOnly} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F2EB]">
      {isReadOnly && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 sticky top-0 z-[60]">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" />
              <span className="text-sm text-amber-800"><strong>Modo visualização:</strong> Seu acesso encontra-se inativo. Regularize sua assinatura para continuar criando novos projetos.</span>
            </div>
            <Button size="sm" onClick={() => navigate("/regularizacao")} className="bg-amber-600 hover:bg-amber-700 text-white">Regularizar</Button>
          </div>
        </div>
      )}
      <header className={`bg-white border-b border-[#E2E8F0] sticky ${isReadOnly ? "top-[52px]" : "top-0"} z-50`}>
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/dashboard")} className="p-2 text-[#718096] hover:text-[#0F4C5C] hover:bg-[#0F4C5C]/5 rounded-lg transition-colors">
                <ArrowLeft size={20} />
              </button>
              <div className="flex items-center gap-2">
                <h1 className="font-serif text-xl font-medium text-[#1A202C] line-clamp-1">{caseData.titulo}</h1>
                {isReadOnly && <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded"><Eye size={12} /> Somente leitura</span>}
              </div>
            </div>
            {!isReadOnly && (
              <Button
                onClick={() => saveCase({
                  identificacao: caseData.identificacao,
                  manifestacoes: caseData.manifestacoes,
                  evidencias: caseData.evidencias,
                  hipoteses: caseData.hipoteses,
                  consolidacao: caseData.consolidacao,
                  metodologia: caseData.metodologia,
                  anexos: caseData.anexos,
                  classificacao_metodologica: caseData.classificacao_metodologica,
                  encerramento: caseData.encerramento
                })}
                disabled={saving}
                variant="ghost"
              >
                <Save size={18} className="mr-2" /> {saving ? "Salvando..." : "Salvar"}
              </Button>
            )}
          </div>
        </div>
      </header>
      <StepNavigation currentStep={caseData.etapa_atual} onStepChange={goToStep} />
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="animate-fade-in">{renderStep()}</div>
      </main>
    </div>
  );
};

export default CasePage;
